import { OptionProps } from '@mui/base';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IconButton } from '@mui/material';
import { EditExternalUserForm } from 'components/Drawer/types';
import dayjs from 'dayjs';
import { Role } from 'graphql/generated/graphqlTypes';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useExternalUsers from 'hooks/useExternalUsers/useExternalUsers';
import useModal from 'hooks/useModal/useModal';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import { sortBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { EditExternalUserDrawer, ExternalUsersGroupsState } from 'state/types';
import { lightMuiTheme } from 'theme/theme';
import DrawerTemplate from 'ui/organisms/Drawer/DrawerTemplate';
import EditExternalUser from 'ui/organisms/Drawer/variants/EditExternalUser';
import {
  handleExpirationDateInputValidation,
  handleNameInputValidation,
  handleSwitchIsAdministratorRoleEnableOnChange,
  handleSwitchIsExpirationDateOnChange
} from '../externalUserGroup/shared';
import { createRoleSelectOptions, getRoleOptionProp } from './shared';

const getInitialRoleSelectValue = (role: Role, translation: Translation) => {
  const roleOptionProp = getRoleOptionProp(role, translation);
  if (roleOptionProp) return roleOptionProp;
  return getRoleOptionProp(Role.ExternalGroupUser, translation) as OptionProps<Role | null>;
};

const getGroupsSelectOptions = (externalUsersGroups: ExternalUsersGroupsState) =>
  sortBy(externalUsersGroups, (user) => user.name.toLocaleLowerCase()).map((group) => ({
    label: group.name,
    value: group.id
  }));

const getInitialGroupsSelectValue = (
  externalUsersGroups: ExternalUsersGroupsState,
  group: string
): OptionProps<string | null> | null => {
  const currentGroup = externalUsersGroups.find((externalGroup) => externalGroup.id === group);
  return currentGroup ? { label: currentGroup.name, value: currentGroup.id } : null;
};

const isMultiAdminEnabled = process.env.REACT_APP_IS_MULTI_ADMIN_ENABLED === 'true';

const EditExternalUserVariant: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const { register, handleSubmit, setValue, control } = useForm<EditExternalUserForm>();
  const { drawerState, hideDrawer } = useDrawer<EditExternalUserDrawer>();
  const { showModal } = useModal();
  const [nameInputValue, setNameInputValue] = useState<string>();
  const [emailInputValue, setEmailInputValue] = useState<string | null>();
  const [mobileDeviceStatus, setMobileDeviceStatus] = useState<boolean>();
  const [nameInputValidation, setNameInputValidation] = useState<string | undefined>(undefined);
  const [isAdministratorRoleEnabled, setIsAdministratorRoleEnabled] = useState<boolean>(
    drawerState?.contentValue.isAdmin
  );
  const [isExpirationDateEnabled, setIsExpirationDateEnabled] = useState<boolean>();
  const [expirationDateInputValue, setExpirationDateInputValue] = useState<dayjs.Dayjs | null>(null);
  const [expirationDateInputValidation, setExpirationDateInputValidation] = useState<string | undefined>(undefined);
  const [showValidation, setShowValidation] = useState(false);
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const {
    externalUsersGroups,
    loadingWithRole: externalUsersGroupsLoading,
    addOrEditVisitor,
    updateVisitorMemberByIdWithRoleLoading
  } = useExternalUsers({
    translation,
    handleFetchError
  });
  const [roleSelectValue, setRoleSelectValue] = useState<OptionProps<Role | null>>(
    drawerState?.contentValue?.role
      ? getInitialRoleSelectValue(drawerState.contentValue.role as Role, translation)
      : getInitialRoleSelectValue(Role.ExternalGroupUser, translation)
  );
  const [groupsSelectValue, setGroupsSelectValue] = useState<null | OptionProps<string | null>>(
    drawerState?.contentValue?.group
      ? getInitialGroupsSelectValue(externalUsersGroups, drawerState.contentValue.group)
      : null
  );
  const [roleInputValidation, setRoleInputValidation] = useState<string | undefined>(undefined);

  const { isAdminEditingHimself } = drawerState.contentValue;

  const onSubmit = async () => {
    setShowValidation(true);
    if (
      !externalUsersGroupsLoading &&
      !updateVisitorMemberByIdWithRoleLoading &&
      emailInputValue !== undefined &&
      nameInputValue &&
      expirationDateInputValidation === undefined &&
      drawerState.contentValue.group &&
      nameInputValidation === undefined &&
      roleSelectValue &&
      roleSelectValue.value &&
      groupsSelectValue &&
      groupsSelectValue.value
    ) {
      try {
        await addOrEditVisitor({
          name: nameInputValue,
          shouldBeAdmin: isAdministratorRoleEnabled,
          visitorGroupMembershipInput: [
            {
              visitorGroupId: groupsSelectValue.value,
              role: roleSelectValue.value
            }
          ],
          email: emailInputValue || undefined,
          expirationDate:
            !isAdministratorRoleEnabled && isExpirationDateEnabled && expirationDateInputValue
              ? expirationDateInputValue
              : undefined,
          id: drawerState.contentValue.id
        });
      } finally {
        hideDrawer();
      }
    }
  };

  const handleDeleteExternalUser = () => {
    showModal({ type: 'deleteExternalUser' });
  };

  const handleRoleSelectChange = (value: OptionProps<Role> | null) => {
    if (value) setRoleSelectValue(value);
    else setRoleSelectValue({ label: '', value: null });
  };

  const handleGroupsSelectChange = (value: OptionProps<string> | null) => {
    if (value) setGroupsSelectValue(value);
    else setGroupsSelectValue(null);
  };

  useEffect(() => {
    if (drawerState && drawerState.contentValue) {
      if (nameInputValue === undefined) setNameInputValue(drawerState.contentValue.name);
      if (emailInputValue === undefined) setEmailInputValue(drawerState.contentValue.email);
      if (mobileDeviceStatus === undefined)
        setMobileDeviceStatus(
          drawerState.contentValue.mobileDevices.find((device) => device.role === 'ACCESS')?.isRegistered
        );
      if (isExpirationDateEnabled === undefined)
        setIsExpirationDateEnabled(Boolean(drawerState.contentValue.expirationDate));
      if (expirationDateInputValue === null)
        setExpirationDateInputValue(
          drawerState.contentValue.expirationDate
            ? dayjs(drawerState.contentValue.expirationDate)
            : dayjs().add(1, 'day')
        );
    }
  }, [
    drawerState,
    emailInputValue,
    expirationDateInputValue,
    isExpirationDateEnabled,
    mobileDeviceStatus,
    nameInputValue
  ]);

  useEffect(() => {
    setMobileDeviceStatus(
      drawerState.contentValue.mobileDevices.find((device) => device.role === 'ACCESS')?.isRegistered
    );
  }, [drawerState, drawerState.contentValue, drawerState.contentValue.mobileDevices]);

  useEffect(() => {
    handleNameInputValidation(translation, setNameInputValidation, nameInputValue);
  }, [nameInputValue, translation]);

  useEffect(() => {
    handleExpirationDateInputValidation(
      translation,
      setExpirationDateInputValidation,
      expirationDateInputValue,
      isExpirationDateEnabled
    );
  }, [expirationDateInputValue, isExpirationDateEnabled, translation]);

  const handleOpenResendInvitationModal = () => {
    if (emailInputValue)
      showModal({
        type: 'resendInvitation',
        contentValue: {
          visitorId: drawerState.contentValue.id,
          email: emailInputValue,
          mobileDevices: drawerState.contentValue.mobileDevices,
          type: 'ACCESS'
        }
      });
  };

  const handleInputExpirationDateToValue = (value: dayjs.Dayjs | null) => {
    if (value) setExpirationDateInputValue(value);
  };

  useEffect(() => {
    if (emailInputValue === '') setRoleInputValidation(translation.role_cannot_be_empty);
    else setRoleInputValidation(undefined);
  }, [emailInputValue, translation.role_cannot_be_empty]);

  useEffect(() => {
    if (nameInputValue) setValue('name', nameInputValue);
    if (emailInputValue) setValue('email', emailInputValue);
  }, [emailInputValue, nameInputValue, setValue]);

  return (
    <DrawerTemplate
      title={translation.edit_external_user}
      headerSideText={
        !isAdminEditingHimself && (
          <IconButton
            size="small"
            style={{ color: lightMuiTheme.palette.error.main }}
            id="drawer-delete-external-group-member"
          >
            <DeleteOutlineIcon fontSize="medium" data-testid="svg-delete" style={{ marginLeft: 0 }} />
          </IconButton>
        )
      }
      headerSideTextOnClick={isAdminEditingHimself ? undefined : handleDeleteExternalUser}
      confirmButtonText={translation.save}
      closeButtonText={translation.cancel}
      closeButton
      id="drawer-edit-external-group-member"
      onSubmit={handleSubmit(onSubmit)}
      disableConfirmButton={showValidation && nameInputValidation !== undefined}
    >
      <EditExternalUser
        register={register}
        nameInputValue={nameInputValue}
        handleNameInputOnChange={setNameInputValue}
        nameInputValidation={nameInputValidation}
        isUserAddedViaKeySharing={drawerState.contentValue.isUserAddedViaKeySharing}
        emailInputValue={emailInputValue}
        status={mobileDeviceStatus}
        showValidation={showValidation}
        handleOnResendInvitationClick={
          drawerState.contentValue.mobileDevices.some(
            (mobileDevice) =>
              (mobileDevice.role === 'ACCESS' && !mobileDevice.isRegistered && mobileDevice.registrationToken) ||
              mobileDevice.isRegistered
          )
            ? handleOpenResendInvitationModal
            : undefined
        }
        inputExpirationDateToValue={expirationDateInputValue}
        handleSwitchIsExpirationDateOnChange={() =>
          handleSwitchIsExpirationDateOnChange(
            setIsExpirationDateEnabled,
            setExpirationDateInputValue,
            expirationDateInputValue,
            isExpirationDateEnabled
          )
        }
        switchIsExpirationDateEnabled={isExpirationDateEnabled}
        handleSwitchIsAdministratorRoleEnabledOnChange={() =>
          handleSwitchIsAdministratorRoleEnableOnChange(setIsAdministratorRoleEnabled, isAdministratorRoleEnabled)
        }
        switchIsAdministratorRoleEnabled={isAdministratorRoleEnabled}
        inputExpirationDateToValueValidation={expirationDateInputValidation}
        handleInputExpirationDateToValue={handleInputExpirationDateToValue}
        control={control}
        roleSelectValue={roleSelectValue}
        roleSelectOption={sortBy(createRoleSelectOptions(translation), (option) => option)}
        handleRoleSelectChange={handleRoleSelectChange}
        groupsSelectValue={groupsSelectValue}
        groupsSelectOption={sortBy(getGroupsSelectOptions(externalUsersGroups), (option) => option)}
        handleGroupsSelectChange={handleGroupsSelectChange}
        roleInputValidation={roleInputValidation}
        isAdminEditingHimself={isAdminEditingHimself}
        isMultiAdminEnabled={isMultiAdminEnabled}
      />
    </DrawerTemplate>
  );
};
export default EditExternalUserVariant;
